import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const AuthModal = ({ isOpen, onClose, defaultView = 'login' }) => {
  const [view, setView] = useState(defaultView);
  const [error, setError] = useState('');
  
  const { signInWithGoogle, authLoading } = useAuth();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset view when defaultView changes
  useEffect(() => {
    setView(defaultView);
  }, [defaultView]);

  if (!isOpen) return null;

  const handleGoogleLogin = async () => {
    setError('');
    const result = await signInWithGoogle();
    if (result.success) {
      onClose();
    } else {
      setError(result.error);
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-md"
        style={{ zIndex: 9999 }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        style={{ zIndex: 10000 }}
      >
        {/* Decorative gradient header */}
        <div className="h-2 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500" />
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-8 py-10">
          {/* Header */}
          <div className="text-center mb-8">
            <img 
              src="/(transparent).png" 
              alt="Growleads.io" 
              className="h-14 w-auto object-contain mb-5 mx-auto"
            />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {view === 'login' ? 'Welcome Back!' : 'Join Growleads.io'}
            </h2>
            <p className="text-gray-500 text-sm">
              {view === 'login' 
                ? 'Sign in to continue using our tools' 
                : 'Create an account to get started'}
            </p>
          </div>

          {/* Benefits for Register */}
          {view === 'register' && (
            <div className="mb-6 py-4 px-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl border border-cyan-100 text-center">
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-600" />
                <span className="font-bold text-gray-900 text-lg">Get 3 Free Credits</span>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <X className="w-3 h-3" />
              </div>
              <span>{error}</span>
            </div>
          )}

          {/* Google Login Button */}
          <button
            onClick={handleGoogleLogin}
            disabled={authLoading}
            className="w-full py-4 bg-white border-2 border-gray-200 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-gray-700 shadow-sm hover:shadow"
          >
            {authLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
                <span>{view === 'login' ? 'Signing in...' : 'Creating account...'}</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </>
            )}
          </button>

          {/* Toggle view */}
          <p className="mt-8 text-center text-sm text-gray-500">
            {view === 'login' ? (
              <>
                New to Growleads?{' '}
                <button
                  onClick={() => setView('register')}
                  className="text-cyan-600 hover:text-cyan-700 font-semibold transition-colors"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  onClick={() => setView('login')}
                  className="text-cyan-600 hover:text-cyan-700 font-semibold transition-colors"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level
  return createPortal(modalContent, document.body);
};

export default AuthModal;
